import { useState } from "react";
import DtoolsReviewLineCard, { DtoolsReviewPrimaryBlock } from "@/components/sales/dtools/DtoolsReviewLineCard";
import { Button } from "@/components/ui/button";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import type { DtoolsReviewSystemView } from "@shared/dtoolsReviewNavigation";
import { ChevronDown } from "lucide-react";

type Props = {
  view: DtoolsReviewSystemView;
};

export default function DtoolsOptionRoomBreakdownSection({ view }: Props) {
  const { overview, tabs } = view;
  const roomTabs = tabs.filter((tab) => tab.kind === "room" || tab.kind === "general");
  const hasRoomDetail = roomTabs.length > 0;
  const hasPricing = overview.roomTotals.length > 0 || overview.servicesTotal > 0;
  const [expanded, setExpanded] = useState(false);

  if (!hasRoomDetail && !hasPricing) return null;

  return (
    <section className="space-y-3 border-t border-[#1A2744]/10 pt-6" style={{ fontFamily: PROPOSAL_COPY_FONT }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em]" style={{ color: PROPOSAL_TEXT }}>
          Room breakdown
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 text-xs font-medium text-[#1A2744]/75"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-hidden
          />
          {expanded ? "Hide room breakdown" : "Show room breakdown"}
        </Button>
      </div>

      {expanded ? (
        <div className="space-y-5 rounded-xl border border-[#1A2744]/10 bg-[#FAFAF7]/60 p-5">
          {hasPricing ? (
            <dl className="space-y-1.5 text-sm">
              {overview.roomTotals.map((row) => (
                <div key={row.name} className="flex items-baseline justify-between gap-4">
                  <dt className="text-[#1A2744]/80">{row.name}</dt>
                  <dd className="font-medium tabular-nums text-[#1A2744]">{formatDtoolsMoney(row.total)}</dd>
                </div>
              ))}
              {overview.servicesTotal > 0 ? (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-[#1A2744]/80">Services</dt>
                  <dd className="font-medium tabular-nums text-[#1A2744]">
                    {formatDtoolsMoney(overview.servicesTotal)}
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}

          {roomTabs.map((tab) => (
            <div key={tab.key} className="space-y-3 border-t border-[#1A2744]/10 pt-4 first:border-t-0 first:pt-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#1A2744]/70">{tab.label}</p>
              <div className="space-y-3">
                {tab.primaries.map((block) => (
                  <DtoolsReviewPrimaryBlock key={block.line.id} block={block} />
                ))}
              </div>
            </div>
          ))}

          {tabs
            .filter((tab) => tab.kind === "services")
            .map((tab) => (
              <div key={tab.key} className="space-y-3 border-t border-[#1A2744]/10 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#1A2744]/70">Services</p>
                <div className="space-y-2">
                  {tab.serviceLines.map((line) => (
                    <DtoolsReviewLineCard key={line.id} line={line} compact />
                  ))}
                </div>
              </div>
            ))}
        </div>
      ) : null}
    </section>
  );
}
